import React, { useState } from 'react';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/apiService';
import ChangePasswordModal from './ChangePasswordModal';
import AuthShell from '../AuthShell';

const MemberLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [uan, setUan] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [memberData, setMemberData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!uan || !uan.trim()) {
      toast.error('Please enter your UAN number');
      return;
    }

    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.memberLogin({
        uan: uan.trim(),
        password
      });

      if (response.mustChangePassword) {
        setMemberData(response.member);
        setShowChangePassword(true);
        return;
      }

      localStorage.setItem('memberToken', response.token);
      localStorage.setItem('member', JSON.stringify(response.member));
      onLogin(response.member);
      toast.success('Login successful!');
    } catch (error) {
      console.error('Member login error:', error);
      toast.error(error.response?.data?.message || 'Failed to login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = (updatedMember) => {
    setShowChangePassword(false);
    localStorage.setItem('member', JSON.stringify(updatedMember));
    onLogin(updatedMember);
    toast.success('Password changed successfully!');
  };

  return (
    <AuthShell
      title="Member portal"
      subtitle="Sign in with your UAN and password"
    >
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="uan" className="block text-sm font-medium text-gray-700 mb-1">
                UAN Number
              </label>
              <input
                id="uan"
                name="uan"
                type="text"
                required
                className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Enter your UAN number"
                value={uan}
                onChange={(e) => setUan(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="appearance-none rounded-lg relative block w-full px-4 py-3 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </span>
              ) : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Login
                </>
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <p className="text-xs text-slate-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Sign up here
              </button>
            </p>
            <p className="text-xs text-slate-500">
              Staff access?{' '}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Open the staff portal
              </button>
            </p>
          </div>
        </form>
      
      {showChangePassword && memberData && (
        <ChangePasswordModal
          member={memberData}
          currentPassword={password}
          onPasswordChanged={handlePasswordChanged}
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </AuthShell>
  );
};

export default MemberLogin;
